import { useCarousel, CarouselNavButtons } from "@/components/ui/Carousel";

/**
 * UniversityReviewCard Component
 */
function UniversityReviewCard({ review }) {
  return (
    <div
      className="bg-white/5 rounded-[1.5rem] p-8 flex flex-col gap-10 h-full min-h-[300px]
                    max-md:p-6 max-md:rounded-[1.2rem] max-md:gap-6 max-md:min-h-[280px]"
    >
      <blockquote
        className="text-[1.7rem] font-family-title leading-[1.35] text-white m-0 font-normal
                            max-md:text-[1.3rem] max-md:leading-[1.35] max-md:hyphens-auto"
      >
        "{review.quote}"
      </blockquote>

      <div className="flex items-center gap-4 mt-auto max-md:gap-4">
        <div className="flex-1">
          <p className="text-[0.85rem] font-semibold text-white m-0 leading-tight max-md:text-[0.75rem]">
            Published by
          </p>
          <div className="flex gap-2 mt-2 max-md:flex-col max-md:gap-1.5">
            <span className="text-[0.85rem] font-normal leading-[1] text-white flex items-center">
              {review.title}
            </span>
            <span
              className="bg-white/[0.04] text-white/50 py-0.5 px-3 rounded-[88rem]
                            text-[0.85rem] font-medium inline-flex items-center w-fit
                            max-md:text-[0.7rem] max-md:py-0.5 max-md:px-2 max-md:mt-0.5"
            >
              {review.university}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * UniversityDots Component
 */
function UniversityDots({ universities, selectedIndex, onSelect }) {
  return (
    <div className="flex justify-center gap-8 mb-14 max-md:mb-5 max-md:gap-6 max-md:px-4">
      {universities.map((uni, index) => (
        <button
          key={index}
          onClick={() => onSelect(index)}
          className="bg-transparent border-none cursor-pointer transition-all py-2
                     flex items-center justify-center hover:-translate-y-0.5
                     max-md:w-[60px] max-md:h-[40px]"
        >
          <img
            src={uni.logo}
            alt={uni.name}
            className={`w-auto h-[30px] object-contain transition-opacity
                       max-md:w-[60px] max-md:h-[40px]
                       ${index === selectedIndex ? "opacity-100" : "opacity-[0.15]"}`}
          />
        </button>
      ))}
    </div>
  );
}

/**
 * UniversityReviewsSection Component
 */
export function UniversityReviewsSection({ reviews }) {
  const isMobile = typeof window !== "undefined" && window.innerWidth <= 767;

  const { emblaRef, selectedIndex, scrollPrev, scrollNext, scrollTo } =
    useCarousel({
      align: "center",
      dragFree: !isMobile,
      loop: true,
      containScroll: true,
    });

  const universities = reviews.map((r) => ({
    name: r.university,
    logo: r.logo,
  }));

  return (
    <section
      className="py-16 pb-10 bg-dark-green rounded-[1.5rem] university-gradient
                        max-md:py-8 max-md:pb-5"
    >
      {/* Header */}
      <div className="text-center mb-8 px-4 max-md:mb-8">
        <h2
          className="font-family-title text-[2.5rem] font-normal text-white mb-3
                       max-md:text-[1.8rem] max-md:leading-[1.25] max-md:mb-2"
        >
          Trusted by Leading Medical Institutions
        </h2>
        <p
          className="text-[1.1rem] text-white/70 max-w-[500px] mx-auto leading-relaxed
                      max-md:text-[0.95rem] max-md:max-w-none"
        >
          Supported by authoritative endorsements from prestigious academic
          institutions and schools.
        </p>
      </div>

      {/* Carousel */}
      <div className="embla p-0">
        {/* University Logo Dots */}
        <UniversityDots
          universities={universities}
          selectedIndex={selectedIndex}
          onSelect={scrollTo}
        />
        <div
          ref={emblaRef}
          className="embla__viewport overflow-hidden cursor-grab select-none"
        >
          <div className="embla__container flex gap-6 max-md:gap-4">
            {reviews.map((review, index) => (
              <div
                key={review.id}
                className={`embla__slide flex-[0_0_700px] min-w-0
                           max-md:flex-[0_0_85%]
                           ${index === 0 ? "ml-6 max-md:ml-4" : ""}`}
              >
                <UniversityReviewCard review={review} />
              </div>
            ))}
          </div>
        </div>

        {/* Navigation */}
        <CarouselNavButtons
          onPrev={scrollPrev}
          onNext={scrollNext}
          variant="university"
          className="max-md:mt-5"
        />
      </div>
    </section>
  );
}

export default UniversityReviewsSection;
